import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BadgeCheck, Instagram, CalendarDays, MessageCircle, MapPin, ChevronDown, ShieldCheck, Building2, Share2, Navigation } from "lucide-react";
import { api, API, fileUrl, COUNTRY_NAME, regLabel, trackWaClick, getOpenStatus } from "@/lib/api";
import { ToothLogo } from "@/components/ToothLogo";
import ImageLightbox from "@/components/ImageLightbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

function PhotoCarousel({ photos, onOpen }) {
  const ref = useRef(null);
  const [idx, setIdx] = useState(0);
  const onScroll = () => {
    const el = ref.current;
    if (el) setIdx(Math.round(el.scrollLeft / el.clientWidth));
  };
  return (
    <div className="relative">
      <div
        ref={ref}
        onScroll={onScroll}
        className="no-scrollbar flex snap-x snap-mandatory overflow-x-auto rounded-[28px]"
      >
        {photos.map((p, i) => (
          <img
            key={i}
            src={fileUrl(p)}
            alt=""
            loading={i === 0 ? "eager" : "lazy"}
            onClick={() => onOpen?.(i)}
            className="aspect-[4/5] w-full shrink-0 cursor-zoom-in snap-center object-cover"
          />
        ))}
      </div>
      {photos.length > 1 && (
        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-1.5">
          {photos.map((_, i) => (
            <span
              key={i}
              className={`h-1 rounded-full transition-all duration-300 ${
                i === idx ? "w-6 bg-white" : "w-3 bg-white/50"
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ActionButton({ icon: Icon, imgSrc, label, onClick, href, testid }) {
  const cls =
    "flex h-[37px] min-w-0 flex-1 items-center justify-center gap-1 rounded-full bg-white px-2 text-[12px] font-medium text-neutral-900 ring-1 ring-black/5 active:scale-95";
  const inner = (
    <>
      {imgSrc ? (
        <img src={imgSrc} alt="" className="h-[18px] w-[18px] shrink-0 object-contain" />
      ) : (
        <Icon className="h-[18px] w-[18px] shrink-0 text-neutral-900" strokeWidth={1.9} />
      )}{" "}
      {label}
    </>
  );
  if (href)
    return (
      <a data-testid={testid} href={href} target="_blank" rel="noreferrer" onClick={onClick} className={cls}>
        {inner}
      </a>
    );
  return (
    <button data-testid={testid} onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}

function ClinicChip({ pro, navigate }) {
  const linked = pro.linked_clinic;
  const name = linked?.name || pro.clinic_name;
  if (!name) return null;
  return (
    <div className="flex items-center gap-3">
      <span className="font-semibold text-neutral-900" style={{ fontSize: 11 }}>Lugar de trabajo</span>
      <button
        data-testid="clinic-tag"
        disabled={!linked}
        onClick={() => linked && navigate(`/perfil/${linked.id}`)}
        className={`rounded-full px-4 py-1.5 text-base font-semibold ${
          linked
            ? "bg-[#F0F0F0] text-neutral-900 active:scale-95"
            : "bg-[#F0F0F0] text-neutral-900"
        }`}
      >
        {name}
      </button>
    </div>
  );
}

export default function Profile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pro, setPro] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [covOpen, setCovOpen] = useState(false);
  const [hoursOpen, setHoursOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/professionals/${id}`).then((r) => setPro(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound)
    return (
      <div className="app-shell grid place-items-center bg-neutral-100" style={{ minHeight: "100dvh" }}>
        <div className="text-center text-neutral-600">
          <ToothLogo size={40} color="#78716c" className="mx-auto mb-3" />
          <p>No encontramos este perfil.</p>
          <button onClick={() => navigate("/")} className="mt-4 rounded-full bg-neutral-900 px-6 py-2 text-white">
            Volver al inicio
          </button>
        </div>
      </div>
    );

  if (!pro)
    return (
      <div className="app-shell space-y-4 bg-neutral-100 p-4" style={{ minHeight: "100dvh" }}>
        <Skeleton className="aspect-[4/5] w-full rounded-[28px]" />
        <Skeleton className="h-40 w-full rounded-[28px]" />
      </div>
    );

  const verified = pro.plan === "verified";
  const isClinic = pro.kind === "clinic";
  const rich = verified || isClinic;
  const whatsapp = pro.whatsapp ? `https://wa.me/${pro.whatsapp.replace(/\D/g, "")}` : null;
  const openStatus = getOpenStatus(pro.schedule);
  const OpenPill = openStatus && (
    <span
      data-testid="open-status"
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${
        openStatus.open ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${openStatus.open ? "bg-emerald-500" : "bg-red-500"}`} />
      {openStatus.open ? "Abierto ahora" : "Cerrado ahora"}
    </span>
  );

  const shareProfile = async () => {
    const url = window.location.href;
    const text = `¡Mira el perfil de ${pro.name} en Odontopick! ${url}`;
    // Same-origin proxy guarantees the photo can be fetched (avoids CORS on external image hosts)
    const imgUrl = pro.photos?.[0] ? `${API}/professionals/${pro.id}/share-image` : null;
    if (navigator.share) {
      try {
        let files;
        if (imgUrl && navigator.canShare) {
          try {
            const resp = await fetch(imgUrl);
            const blob = await resp.blob();
            const file = new File([blob], `${pro.name}.jpg`, { type: blob.type || "image/jpeg" });
            if (navigator.canShare({ files: [file] })) files = [file];
          } catch (e) {
            /* fall back to link-only share */
          }
        }
        await navigator.share(files ? { title: pro.name, text, url, files } : { title: pro.name, text, url });
        return;
      } catch (e) {
        return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Enlace copiado");
    } catch (e) {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const BackBtn = (
    <button
      data-testid="back-btn"
      onClick={() => navigate(-1)}
      aria-label="Volver"
      className="fixed bottom-6 left-1/2 z-30 grid -translate-x-1/2 place-items-center rounded-full bg-white/40 text-neutral-900 backdrop-blur active:scale-95"
      style={{ width: 90, height: 56 }}
    >
      <img src="/assets/tooth-back.png" alt="Volver" className="object-contain" style={{ width: 23, height: 23 }} />
    </button>
  );

  const ShareBtn = (
    <button
      data-testid="share-btn"
      onClick={shareProfile}
      aria-label="Compartir"
      className="absolute right-4 top-6 z-30 grid h-11 w-11 place-items-center rounded-full bg-white/80 text-neutral-900 shadow-md backdrop-blur active:scale-95"
    >
      <Share2 className="h-5 w-5" strokeWidth={2} />
    </button>
  );

  /* ---------------- Free layout ---------------- */
  if (!rich) {
    const photo = pro.photos?.[0];
    return (
      <div className="app-shell relative flex flex-col bg-neutral-100" style={{ minHeight: "100dvh" }}>
        {BackBtn}
        {ShareBtn}
        <div className="flex flex-1 items-center justify-center pt-10">
          {photo ? (
            <img src={fileUrl(photo)} alt={pro.name} className="h-44 w-44 rounded-[36px] object-cover" />
          ) : (
            <img
              src="/assets/tooth-placeholder.png"
              alt=""
              className="h-16 w-16 object-contain opacity-25"
            />
          )}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          className="safe-bottom rounded-t-[34px] bg-[#DEDEDE] px-6 pb-8 pt-7"
        >
          <div className="flex items-center gap-1.5">
            <h1 className="font-brand text-3xl font-semibold text-neutral-900">{pro.name}</h1>
          </div>
          <p className="mt-1 text-sm text-neutral-400">{regLabel(pro.country)} {pro.senacyt || "—"}</p>
          <p className="mt-4 text-base text-neutral-800">
            {pro.zone ? `${pro.zone}, ` : ""}{pro.city}. {COUNTRY_NAME[pro.country]} <span className="mx-1 text-neutral-400">|</span> {pro.specialties?.[0]}
          </p>
          {OpenPill && <div className="mt-3">{OpenPill}</div>}
          <div className="mt-5">
            <ClinicChip pro={pro} navigate={navigate} />
          </div>
          {pro.address && (
            <p className="mt-5 text-base text-neutral-800">
              <span className="font-semibold">Dirección: </span>
              {pro.address}
            </p>
          )}
          {whatsapp && (
            <div className="mt-7 flex justify-end">
              <a
                data-testid="whatsapp-btn"
                href={whatsapp}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackWaClick(pro.id)}
                className="flex items-center justify-center gap-2 rounded-full bg-white text-sm font-medium text-neutral-900 shadow-md active:scale-95"
                style={{ width: 105, height: 37 }}
              >
                <img src="/assets/whatsapp.png" alt="" className="h-4 w-4 shrink-0 object-contain" /> Whatsapp
              </a>
            </div>
          )}
          <div className="h-24 shrink-0" />
        </motion.div>
      </div>
    );
  }

  /* ---------------- Verified / Clinic layout ---------------- */
  const photos = pro.photos?.length ? pro.photos : [];
  const photoImgs = photos.map(fileUrl);
  const galleryImgs = (pro.gallery || []).slice(0, 4).map(fileUrl);
  const mapQuery = pro.address || `${pro.name} ${pro.city} ${COUNTRY_NAME[pro.country] || ""}`;
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}&output=embed`;
  return (
    <div className="app-shell relative bg-neutral-100 pb-10" style={{ minHeight: "100dvh" }}>
      {BackBtn}
      {ShareBtn}
      <div className="relative p-3">
        {photos.length > 0 ? (
          <PhotoCarousel photos={photos} onOpen={(idx) => setLightbox({ images: photoImgs, index: idx })} />
        ) : (
          <div className="grid aspect-[4/5] w-full place-items-center rounded-[28px] bg-neutral-200">
            {verified ? (
              <ToothLogo size={90} color="#0a0a0a" />
            ) : (
              <img
                src="/assets/tooth-placeholder.png"
                alt=""
                className="h-16 w-16 object-contain opacity-25"
              />
            )}
          </div>
        )}
        {OpenPill && <div className="absolute left-6 top-6 z-10">{OpenPill}</div>}
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative mx-3 rounded-[28px] bg-[#DEDEDE] p-6"
      >
        <div className="flex items-center gap-1.5">
          <h1 className="font-brand font-normal text-neutral-900" style={{ fontSize: 26 }}>{pro.name}</h1>
          {verified && <BadgeCheck className="h-6 w-6 shrink-0 fill-neutral-900 text-white" />}
          {isClinic && <Building2 className="h-5 w-5 text-neutral-500" />}
        </div>
        <p className="mt-1 text-sm text-neutral-400">{regLabel(pro.country)} {pro.senacyt || "—"}</p>

        <p className="mt-4 text-sm text-neutral-800">
          {pro.zone ? `${pro.zone}, ` : ""}{pro.city}. {COUNTRY_NAME[pro.country]}
          {pro.specialties?.[0] && (
            <>
              <span className="mx-2 text-neutral-400">|</span>
              {pro.specialties[0]}
            </>
          )}
        </p>

        {!isClinic && (
          <div className="mt-5">
            <ClinicChip pro={pro} navigate={navigate} />
          </div>
        )}

        {pro.description && <p className="mt-5 whitespace-pre-wrap text-base leading-relaxed text-neutral-800">{pro.description}</p>}

        {/* Coverage */}
        {pro.insurances?.length > 0 && (
          <button
            data-testid="coverage-btn"
            onClick={() => setCovOpen(true)}
            className="mt-5 flex items-center gap-1 font-brand font-normal text-neutral-900"
            style={{ fontSize: 17 }}
          >
            Atención / Cobertura <ChevronDown className="h-5 w-5" />
          </button>
        )}

        {/* Actions */}
        <div className="mt-6 flex flex-nowrap gap-2">
          {pro.instagram && (
            <ActionButton testid="instagram-btn" icon={Instagram} label="Instagram" href={pro.instagram} />
          )}
          {(pro.schedule?.length || pro.hours) && (
            <ActionButton testid="hours-btn" icon={CalendarDays} label="Horarios" onClick={() => setHoursOpen(true)} />
          )}
          {whatsapp && <ActionButton testid="whatsapp-btn" imgSrc="/assets/whatsapp.png" label="Whatsapp" href={whatsapp} onClick={() => trackWaClick(pro.id)} />}
        </div>

        {/* Gallery (before map) */}
        {pro.gallery?.length > 0 && (
          <div className="mt-6 grid grid-cols-4 gap-2" data-testid="gallery">
            {pro.gallery.slice(0, 4).map((g, i) => (
              <button
                key={i}
                type="button"
                data-testid={`gallery-photo-${i}`}
                onClick={() => setLightbox({ images: galleryImgs, index: i })}
                className="aspect-[3/4] overflow-hidden rounded-[9px] ring-1 ring-black/5 active:scale-95"
              >
                <img src={fileUrl(g)} alt="" loading="lazy" className="h-full w-full rounded-[9px] object-cover" />
              </button>
            ))}
          </div>
        )}

        {/* Address (under the 4-photo gallery) */}
        {pro.address && (
          <p className="mt-5 text-sm text-neutral-800" data-testid="verified-address">
            <span className="font-semibold">Dirección: </span>
            {pro.address}
          </p>
        )}

        {/* Embedded map (compact) */}
        {(pro.map_url || pro.address) && (
          <div className="mt-[30px]" data-testid="embedded-map">
            <div className="relative overflow-hidden rounded-2xl ring-1 ring-black/5">
              <iframe
                title="Mapa"
                src={mapSrc}
                className="h-36 w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
              <a
                data-testid="open-maps-link"
                href={pro.map_url || `https://www.google.com/maps?q=${encodeURIComponent(mapQuery)}`}
                target="_blank"
                rel="noreferrer"
                className="absolute right-3 top-3 z-20 flex items-center gap-2 rounded-full bg-neutral-900 px-4 py-2 font-normal text-white shadow-lg active:scale-95"
                style={{ fontSize: 11 }}
              >
                <img src="/assets/maps.png" alt="" className="h-4 w-4 object-contain" /> Cómo llegar
              </a>
            </div>
          </div>
        )}

        {/* Clinic members */}
        {isClinic && pro.members?.length > 0 && (
          <div className="mt-7">
            <h3 className="font-brand text-lg font-semibold text-neutral-900">Odontólogos</h3>
            <div className="mt-3 space-y-3">
              {pro.members.map((m) => (
                <button
                  key={m.id}
                  data-testid={`clinic-member-${m.id}`}
                  onClick={() => navigate(`/perfil/${m.id}`)}
                  className="flex w-full items-center gap-3 rounded-2xl bg-white p-3 text-left shadow-sm active:scale-[0.99]"
                >
                  {m.photos?.[0] ? (
                    <img src={fileUrl(m.photos[0])} alt={m.name} className="h-12 w-12 rounded-xl object-cover" />
                  ) : (
                    <span className="grid h-12 w-12 place-items-center rounded-xl bg-neutral-100">
                      <ToothLogo size={22} color="#a3a3a3" />
                    </span>
                  )}
                  <div>
                    <p className="font-brand font-semibold text-neutral-900">{m.name}</p>
                    <p className="text-sm text-neutral-500">{m.specialties?.[0]}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* Coverage modal */}
      <Dialog open={covOpen} onOpenChange={setCovOpen}>
        <DialogContent className="mx-auto max-w-[420px] rounded-3xl" data-testid="coverage-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-brand text-xl font-normal">
              <ShieldCheck className="h-5 w-5" style={{ color: "#5C5CF2" }} /> Atención / Cobertura
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-wrap gap-2 pt-2">
            {pro.insurances?.map((i) => (
              <span key={i} className="rounded-full bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-800">
                {i}
              </span>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Hours modal */}
      <Dialog open={hoursOpen} onOpenChange={setHoursOpen}>
        <DialogContent className="mx-auto max-w-[420px] rounded-3xl" data-testid="hours-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-brand text-xl">
              <CalendarDays className="h-5 w-5" /> Horarios de atención
            </DialogTitle>
          </DialogHeader>
          {pro.schedule?.length > 0 ? (
            <div className="divide-y divide-neutral-100 pt-1">
              {pro.schedule.map((s) => (
                <div key={s.day} className="flex items-center justify-between py-2.5">
                  <span className="text-base font-medium text-neutral-800">{s.day}</span>
                  <span className="text-base text-neutral-600">{s.from} – {s.to}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="pt-1 text-base text-neutral-700">{pro.hours}</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Fullscreen image viewer with zoom + swipe */}
      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          index={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
